/**
 * The two accounts a ServeFirst person can have: an admin app account (the sign-in service user)
 * and a web app account (a platform super admin in sf-api). The sign-in service's `GET /users`
 * with `group=servefirst` sends both on every row; rows from an older sign-in service, or from
 * `group=platform`, leave out what they do not know.
 */

/** The tab the Users page lists, sent to the sign-in service as `?group=`. */
export type UserGroup = 'servefirst' | 'platform';
export const DEFAULT_GROUP: UserGroup = 'servefirst';
export const isUserGroup = (value: unknown): value is UserGroup =>
  value === 'servefirst' || value === 'platform';

/** The person's web app account, from sf-api's platform super admin list. */
export interface PlatformAccount {
  active: boolean;
  kind: 'full' | 'limited';
}

export interface AccountRow {
  name?: string | null;
  email: string;
  status?: string;
  /** false: listed from the web app only, they have never signed in to the admin app. */
  adminAppAccount?: boolean;
  /** null: no web app super admin account. Missing: the service did not say. */
  platformAccount?: PlatformAccount | null;
}

/** The permission `POST /users/deactivate` and `/users/reactivate` need on the sign-in service. */
export const ACCOUNT_SWITCH_PERMISSION = 'admin:users:*';

export const NO_ADMIN_APP_ACCOUNT_TITLE =
  'Has no admin app account yet. They get one the first time they sign in.';

export const hasNoAdminAppAccount = (row: AccountRow) => row.adminAppAccount === false;

const normalizeEmail = (email: string | null | undefined) => (email ?? '').trim().toLowerCase();

export const sameEmail = (a: string | null | undefined, b: string | null | undefined) =>
  normalizeEmail(a) !== '' && normalizeEmail(a) === normalizeEmail(b);

/**
 * True only when every account the person can have is known to be switched off, so there is
 * nothing left to deactivate. Unknown counts as still on: the button stays, and the server call
 * is safe to repeat.
 *
 * The web app side is known off when `platformAccount` says `active: false`, or when it is null
 * as the ServeFirst tab sends it (the service checked: no web app super admin). Missing: the
 * service did not say, so it may still be on.
 */
export const isSwitchedOffEverywhere = (row: AccountRow) => {
  const adminAppOff = hasNoAdminAppAccount(row) || row.status === 'inactive';
  const webAppOff = row.platformAccount?.active === false || row.platformAccount === null;
  return adminAppOff && webAppOff;
};

/**
 * True when Reactivate may have something to switch back on: a deactivated admin app account
 * (`inactive`; a suspended one is left alone by the server, as SF-5059 asks), a web app account
 * that is off, or a web app account we could not see (missing `platformAccount`: not on the
 * ServeFirst list, for example a customer). Reactivate only switches on what is off, so offering
 * it for an unknown account is safe.
 */
export const hasSwitchedOffAccount = (row: AccountRow) =>
  (!hasNoAdminAppAccount(row) && row.status === 'inactive') ||
  row.platformAccount === undefined ||
  row.platformAccount?.active === false;

/** Which way `POST /users/deactivate` or `POST /users/reactivate` switches the accounts. */
export type AccountSwitch = 'deactivate' | 'reactivate';

/** The answer of both routes: what each account had and whether this call changed it. */
export interface AccountSwitchResult {
  success?: boolean;
  adminApp?: { found?: boolean; changed?: boolean };
  platform?: { found?: boolean; changed?: boolean };
}

export const accountSwitchDoneMessage = (
  action: AccountSwitch,
  label: string,
  result: AccountSwitchResult | undefined,
) => {
  const changed = [
    result?.adminApp?.changed ? 'admin app account' : null,
    result?.platform?.changed ? 'web app account' : null,
  ].filter((part): part is string => part !== null);
  if (action === 'reactivate') {
    // Not "already on": a suspended account is left as it is, and stays unable to sign in.
    if (changed.length === 0) return `${label}: Nothing was switched off, so nothing changed`;
    return `${label}: switched their ${changed.join(' and ')} back on`;
  }
  if (changed.length === 0) return `${label}: Already switched off`;
  return `${label}: switched off their ${changed.join(' and ')}`;
};

/** The sign-in service answers errors as { error: { code, message, status } }. */
export const accountSwitchErrorMessage = (error: unknown) => {
  const message = (error as { response?: { data?: { error?: { message?: unknown } } } })?.response
    ?.data?.error?.message;
  return typeof message === 'string' && message.trim() ? message : 'Please try again.';
};
